import Service from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import moment from 'moment';

export interface ColumnMapping {
  column: string;
  property: string;
  exampleValues: string[];
}

export default class UploadContactsService extends Service {
  @tracked contactObjects: any = [];
  @tracked columnMappings: ColumnMapping[] = [];
  @tracked columns: string[] = [];
  @tracked fileData: any = [];

  get activeColumnMappings() {
    return this.columnMappings.filter((mapping: ColumnMapping) => {
      return !isEmpty(mapping.property);
    });
  }

  get propertyOptions() {
    // defined at the bottom of this file
    return propertyOptions;
  }

  get activePropertyOptions() {
    let propertyNames: Record<string, boolean> = {};
    this.activeColumnMappings.forEach((mapping: ColumnMapping) => {
      propertyNames[mapping.property] = true;
    });
    return this.propertyOptions.filter((item) => {
      return propertyNames[item.val];
    });
  }

  get importGroupName() {
    return `CSV Import ${moment().format('YYYY-MM-DD')}`;
  }

  reset() {
    this.fileData = [];
    this.columns = [];
    this.columnMappings = [];
    this.contactObjects = [];
  }

  populateColumnMappings() {
    const mappings: ColumnMapping[] = [];

    this.columns.forEach((column: string) => {
      mappings.push({
        column: column,
        property: '',
        exampleValues: this.getExampleValues(column),
      });
    });
    this.columnMappings = mappings;
  }

  getExampleValues(columnName: string) {
    const examples: string[] = [];
    const fileData = this.fileData;

    for (let i = 0; i < fileData.length; i++) {
      const val = fileData[i][columnName];

      if (!isEmpty(val)) {
        examples.push(val);
      }
      if (examples.length > 4) {
        i = fileData.length;
      }
    }

    return examples;
  }

  createContactObjects() {
    let contacts: any[] = [];
    let nameMapping = this.activeColumnMappings.find((item) => {
      return item.property === 'name';
    });
    let activeMappings = this.activeColumnMappings.filter((item) => {
      let property = item.property;
      return !isEmpty(property) && property !== 'name';
    });
    let importGroupName = this.importGroupName;

    this.fileData.forEach((row: Record<string, string>) => {
      const name = (row[nameMapping!.column] || '').trim();

      if (!isEmpty(name)) {
        let contact: any = {
          name: name,
          active: true,
          phones: { data: [] },
          emails: { data: [] },
          groups: [importGroupName],
          applyMailchimpAutomaticSyncSettings: true,
        };

        activeMappings.forEach((mapping) => {
          let val = (row[mapping.column] || '').trim();
          if (!isEmpty(val)) {
            if (mapping.property.indexOf('phone') === 0) {
              contact.phones.data.push({ v: val });
            } else if (mapping.property.indexOf('email') === 0) {
              contact.emails.data.push({ v: val });
            } else {
              contact[mapping.property] = val;
            }
          }
        });

        // confirm zip is not too long
        if (contact.zip && contact.zip.length > 13) {
          contact.zip = contact.zip.substring(0, 13);
        }

        contacts.push(contact);
      }
    });
    this.contactObjects = contacts;
  }
}

const propertyOptions = [
  {
    label: '',
    val: '',
  },
  {
    label: 'Display Name',
    val: 'name',
  },
  {
    label: 'Mailing Name',
    val: 'mailingName',
  },
  {
    label: 'Greeting',
    val: 'greeting',
  },
  {
    label: 'First Name',
    val: 'firstName',
  },
  {
    label: 'Last Name',
    val: 'lastName',
  },
  {
    label: 'Spouse First Name',
    val: 'spouseFirstName',
  },
  {
    label: 'Spouse Last Name',
    val: 'spouseLastName',
  },
  {
    label: 'Address Line 1',
    val: 'address1',
  },
  {
    label: 'Address Line 2',
    val: 'address2',
  },
  {
    label: 'City',
    val: 'city',
  },
  {
    label: 'State',
    val: 'state',
  },
  {
    label: 'Zip',
    val: 'zip',
  },
  {
    label: 'Country',
    val: 'country',
  },
  {
    label: 'Phone',
    val: 'phone1',
  },
  {
    label: 'Phone',
    val: 'phone2',
  },
  {
    label: 'Email',
    val: 'email1',
  },
  {
    label: 'Email',
    val: 'email2',
  },
];
